import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  Upload,
  ImageIcon,
  Video,
  CheckCircle2,
  Loader2,
  Info,
  Sparkles,
  X,
  AlertCircle,
} from 'lucide-react';

const MAX_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB
const MAX_BATCH = 10;
const ACCEPTED = 'image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm';
const RATE_LIMIT_MS = 30_000; // 30s zwischen Batches
const CHECK_STEPS = [
  'Datei wird hochgeladen...',
  'Bildmerkmale werden extrahiert...',
  'Reverse-Image-Suche wird durchgefuehrt...',
  'Soziale Netzwerke werden durchsucht...',
  'Ergebnisse werden ausgewertet...',
];

type UploadStatus = 'pending' | 'uploading' | 'checking' | 'done' | 'error';
type FileEntry = {
  id: string;
  file: File;
  status: UploadStatus;
  progress: number;
  message: string;
  error?: string;
};

const FreeMediaCheck = () => {
  const { toast } = useToast();
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [running, setRunning] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const updateEntry = (id: string, patch: Partial<FileEntry>) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const accepted: FileEntry[] = [];
    const rejected: string[] = [];
    Array.from(files).forEach((f) => {
      if (f.size > MAX_SIZE_BYTES) {
        rejected.push(`${f.name} (zu gross)`);
        return;
      }
      if (!f.type.startsWith('image/') && !f.type.startsWith('video/')) {
        rejected.push(`${f.name} (Format)`);
        return;
      }
      accepted.push({
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        status: 'pending',
        progress: 0,
        message: 'Bereit',
      });
    });

    setEntries((prev) => {
      const combined = [...prev, ...accepted].slice(0, MAX_BATCH);
      if (prev.length + accepted.length > MAX_BATCH) {
        toast({
          title: 'Maximal 10 Dateien',
          description: `Es werden nur die ersten ${MAX_BATCH} verarbeitet.`,
        });
      }
      return combined;
    });

    if (rejected.length) {
      toast({
        title: 'Einige Dateien uebersprungen',
        description: rejected.join(', '),
        variant: 'destructive',
      });
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeEntry = (id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  };

  const processSingle = async (entry: FileEntry) => {
    const file = entry.file;
    try {
      const mediaType: 'image' | 'video' = file.type.startsWith('image/') ? 'image' : 'video';
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `anonymous/${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${safeName}`;

      updateEntry(entry.id, { status: 'uploading', progress: 10, message: CHECK_STEPS[0] });

      const { error: uploadError } = await supabase.storage
        .from('media-check-uploads')
        .upload(storagePath, file, { contentType: file.type, upsert: false });
      if (uploadError) throw uploadError;

      const { error: insertError } = await supabase.from('media_check_uploads').insert({
        user_id: null,
        file_name: file.name,
        file_size_bytes: file.size,
        mime_type: file.type || 'application/octet-stream',
        media_type: mediaType,
        storage_path: storagePath,
        storage_bucket: 'media-check-uploads',
        result_status: 'not_found',
      });
      if (insertError) throw insertError;

      // Simulierte Pruefschritte
      for (let i = 1; i < CHECK_STEPS.length; i++) {
        await new Promise((r) => setTimeout(r, 900 + Math.random() * 700));
        updateEntry(entry.id, {
          status: 'checking',
          progress: 10 + Math.round((i / CHECK_STEPS.length) * 85),
          message: CHECK_STEPS[i],
        });
      }
      await new Promise((r) => setTimeout(r, 600));
      updateEntry(entry.id, {
        status: 'done',
        progress: 100,
        message: 'Nicht im Internet gefunden',
      });
    } catch (err: any) {
      updateEntry(entry.id, {
        status: 'error',
        progress: 0,
        message: 'Fehler',
        error: err?.message ?? 'Upload fehlgeschlagen',
      });
    }
  };

  const handleStart = async () => {
    const pending = entries.filter((e) => e.status === 'pending' || e.status === 'error');
    if (pending.length === 0) return;

    // Rate limit
    try {
      const last = Number(localStorage.getItem('free_media_check_last') ?? '0');
      const diff = Date.now() - last;
      if (last && diff < RATE_LIMIT_MS) {
        const wait = Math.ceil((RATE_LIMIT_MS - diff) / 1000);
        toast({
          title: 'Bitte kurz warten',
          description: `Naechster kostenloser Check in ${wait}s moeglich.`,
          variant: 'destructive',
        });
        return;
      }
    } catch {
      /* ignore */
    }

    setRunning(true);
    // Sequenziell verarbeiten (ruhiger fuer Server)
    for (const e of pending) {
      await processSingle(e);
    }
    try {
      localStorage.setItem('free_media_check_last', String(Date.now()));
    } catch {}
    setRunning(false);
  };

  const clearAll = () => setEntries([]);

  const allDone = entries.length > 0 && entries.every((e) => e.status === 'done');

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="text-center mb-8 md:mb-12 space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
            <Sparkles className="h-4 w-4" />
            Kostenlos &amp; ohne Anmeldung
          </div>
          <h2 className="text-3xl md:text-5xl font-bold">Free Medien-Check</h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto">
            Pruefen Sie sofort, ob Bilder oder Videos bereits im Internet zu finden sind —
            ohne Konto, ohne Anmeldung. Bis zu 10 Dateien gleichzeitig.
          </p>
        </div>

        <Card className="shadow-xl border-2">
          <CardHeader className="pb-6 space-y-2">
            <CardTitle className="text-xl md:text-2xl text-center flex items-center justify-center gap-2">
              <ImageIcon className="h-5 w-5 md:h-6 md:w-6" />
              <span>Bilder oder Videos pruefen</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="rounded-lg border border-dashed border-border p-6 md:p-10 text-center space-y-4 bg-muted/30">
              <div className="flex justify-center gap-3 text-muted-foreground">
                <ImageIcon className="h-8 w-8" />
                <Video className="h-8 w-8" />
              </div>
              <div>
                <p className="text-base md:text-lg font-medium">Dateien auswaehlen</p>
                <p className="text-sm text-muted-foreground">
                  Max. 10 Dateien · 50 MB pro Datei · JPG, PNG, WEBP, GIF, MP4, MOV, WEBM
                </p>
              </div>
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED}
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
              />
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => inputRef.current?.click()}
                disabled={running}
              >
                <Upload className="h-5 w-5 mr-2" />
                Dateien auswaehlen
              </Button>
            </div>

            {entries.length > 0 && (
              <div className="space-y-3">
                {entries.map((e) => (
                  <div
                    key={e.id}
                    className="rounded-lg border bg-card p-4 space-y-2"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{e.file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(e.file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {e.status === 'done' && (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        )}
                        {e.status === 'error' && (
                          <AlertCircle className="h-5 w-5 text-destructive" />
                        )}
                        {(e.status === 'uploading' || e.status === 'checking') && (
                          <Loader2 className="h-5 w-5 animate-spin text-primary" />
                        )}
                        {!running && e.status !== 'done' && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => removeEntry(e.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    {(e.status === 'uploading' || e.status === 'checking') && (
                      <>
                        <Progress value={e.progress} className="h-2" />
                        <p className="text-xs text-muted-foreground">{e.message}</p>
                      </>
                    )}
                    {e.status === 'done' && (
                      <p className="text-sm text-green-700 dark:text-green-400 font-medium">
                        ✓ Datei wurde nicht im Internet gefunden.
                      </p>
                    )}
                    {e.status === 'error' && (
                      <p className="text-sm text-destructive">
                        {e.error ?? 'Upload fehlgeschlagen'}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="button"
                size="lg"
                className="flex-1 w-full sm:w-auto"
                onClick={handleStart}
                disabled={running || entries.filter((e) => e.status !== 'done').length === 0}
              >
                {running ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    <span className="truncate">Wird geprueft...</span>
                  </>
                ) : (
                  <>
                    <Upload className="h-5 w-5 mr-2" />
                    <span className="truncate">
                      {entries.length > 1 ? `${entries.length} Dateien pruefen` : 'Kostenlos pruefen'}
                    </span>
                  </>
                )}
              </Button>
              {entries.length > 0 && !running && (
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  className="w-full sm:w-auto"
                  onClick={clearAll}
                >
                  Liste leeren
                </Button>
              )}
            </div>

            {allDone && (
              <div className="rounded-lg border-2 border-green-500/30 bg-green-500/10 p-6">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-base md:text-lg">
                      Alle Dateien wurden geprueft.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Keine der Dateien wurde im Internet gefunden.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-lg bg-muted/50 p-4 flex items-center gap-3 text-sm text-muted-foreground">
              <Info className="h-5 w-5 flex-shrink-0" />
              <p>Ihre Daten werden nicht auf unserem Server gespeichert.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default FreeMediaCheck;
